'use client';

import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { createClient } from '@/lib/supabase/client';
import type { Employee, EmployeeFullCertification } from './types';

interface CertificationModalProps {
  employee: Employee | null;
  existingCert: EmployeeFullCertification | null;
  onClose: () => void;
  onSaved: () => void;
}

const CERT_TYPES = [
  { key: 'osha_10',         label: 'OSHA 10 - Construction' },
  { key: 'osha_30',         label: 'OSHA 30 - Construction' },
  { key: 'osha_other',      label: 'Other OSHA' },
  { key: 'manufacturer',    label: 'Manufacturer Cert' },
  { key: 'state_license',   label: 'State License' },
  { key: 'first_aid',       label: 'First Aid' },
  { key: 'cpr',             label: 'CPR' },
  { key: 'fall_protection', label: 'Fall Protection' },
  { key: 'forklift',        label: 'Forklift' },
  { key: 'cdl',             label: 'CDL' },
  { key: 'other',           label: 'Other' },
];

const CertificationModal = ({ employee, existingCert, onClose, onSaved }: CertificationModalProps) => {
  const isEdit = !!existingCert;

  const [certName, setCertName] = useState('');
  const [certType, setCertType] = useState('osha_10');
  const [issuingOrg, setIssuingOrg] = useState('');
  const [certNumber, setCertNumber] = useState('');
  const [issuedDate, setIssuedDate] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const supabase = createClient();

  useEffect(() => {
    if (!employee) return;
    setSubmitting(false);
    if (existingCert) {
      setCertName(existingCert.certification_name ?? '');
      setCertType(existingCert.certification_type ?? 'other');
      setIssuingOrg(existingCert.issuing_organization ?? '');
      setCertNumber(existingCert.certification_number ?? '');
      setIssuedDate(existingCert.issued_date?.slice(0, 10) ?? '');
      setExpirationDate(existingCert.expiration_date?.slice(0, 10) ?? '');
      setNotes(existingCert.notes ?? '');
    } else {
      setCertName('');
      setCertType('osha_10');
      setIssuingOrg('');
      setCertNumber('');
      setIssuedDate(new Date().toISOString().slice(0, 10));
      setExpirationDate('');
      setNotes('');
    }
  }, [employee?.id, existingCert?.id]);

  useEffect(() => {
    if (!employee) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [employee, onClose]);

  if (!employee) return null;

  const isValid = certName.trim().length >= 2 && !!certType && !submitting;

  const saveWithRetry = async () => {
    if (!isValid) return;
    setSubmitting(true);

    const payload: any = {
      employee_id: employee.id,
      // Don't set any FK column from auth — currentUser.id points to user_profiles.id,
      // not employees.id. Any FK column referencing employees.id with an unmatched UUID
      // causes the insert to hang. Leave such columns null.
      certification_name: certName.trim(),
      certification_type: certType,
      issuing_organization: issuingOrg.trim() || null,
      certification_number: certNumber.trim() || null,
      issued_date: issuedDate || null,
      expiration_date: expirationDate || null,
      status: 'active',
      notes: notes.trim() || null,
      updated_at: new Date().toISOString(),
    };

    console.log(`[CertModal] Target employee_id: ${employee.id} (employee table)`);
    console.log(`[CertModal] Saving (${isEdit ? 'edit' : 'create'}). Full payload below:`);
    console.log('[CertModal] payload JSON:', JSON.stringify(payload, null, 2));
    console.log('[CertModal] payload keys:', Object.keys(payload));
    console.log('[CertModal] payload values:', Object.entries(payload).map(([k, v]) => `${k}: ${typeof v} = ${JSON.stringify(v)}`));

    // Connectivity probe — verify Supabase is reachable before attempting insert
    console.log('[CertModal] Testing Supabase connectivity from browser...');
    try {
      const testStart = Date.now();
      const testResult = await Promise.race([
        supabase.from('employees').select('id').eq('id', employee.id).single(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Connectivity test timed out')), 5000)),
      ]);
      console.log(`[CertModal] Connectivity test passed in ${Date.now() - testStart}ms:`, testResult);
    } catch (testErr) {
      console.error('[CertModal] Connectivity test FAILED:', testErr);
    }

    for (let attempt = 0; attempt < 3; attempt++) {
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(`Save timed out (attempt ${attempt + 1}/3)`)), 8000)
      );

      const opPromise = isEdit
        ? supabase
            .from('employee_certifications')
            .update(payload)
            .eq('id', existingCert!.id)
            .select()
            .single()
            .then((res: any) => { if (res.error) throw res.error; return res.data; })
        : supabase
            .from('employee_certifications')
            .insert(payload)
            .select()
            .single()
            .then((res: any) => { if (res.error) throw res.error; return res.data; });

      try {
        await Promise.race([opPromise, timeoutPromise]);
        console.log(`[CertModal] Save succeeded attempt ${attempt + 1}`);
        toast.success(isEdit ? 'Certification updated' : 'Certification added', {
          duration: 3000,
          icon: '🎓',
        });
        onSaved();
        setSubmitting(false);
        onClose();
        return;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        const isRateLimit = msg.includes('429') || msg.toLowerCase().includes('too many');
        const isTimeout = msg.includes('timed out');
        console.warn(`[CertModal] Attempt ${attempt + 1} failed: ${msg}`);
        console.warn('[CertModal] Full error object:', err);
        if (err && typeof err === 'object') {
          console.warn('[CertModal] error.code:', (err as any).code);
          console.warn('[CertModal] error.message:', (err as any).message);
          console.warn('[CertModal] error.details:', (err as any).details);
          console.warn('[CertModal] error.hint:', (err as any).hint);
        }
        if (attempt === 2 || (!isRateLimit && !isTimeout)) {
          toast.error(`Save failed: ${msg}`, { duration: 6000 });
          setSubmitting(false);
          return;
        }
        await new Promise(r => setTimeout(r, 500 * Math.pow(2, attempt)));
      }
    }
  };

  const handleRenew = () => {
    if (!existingCert) return;
    const base =
      expirationDate && new Date(expirationDate).getTime() > Date.now()
        ? new Date(expirationDate)
        : new Date();
    const next = new Date(base);
    next.setFullYear(base.getFullYear() + 1);
    setExpirationDate(next.toISOString().slice(0, 10));
    setIssuedDate(new Date().toISOString().slice(0, 10));
    toast(`Renewed: expiration set to ${next.toLocaleDateString()}`, { icon: '📅', duration: 2500 });
  };

  const inputClass =
    'w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all';

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[120] flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cert-modal-title"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-gray-100">
          <div>
            <h3 id="cert-modal-title" className="text-lg font-bold text-[#1C2E5E]">
              {isEdit ? 'Edit Certification' : 'Add Certification'}
            </h3>
            <p className="text-[12px] text-gray-500 mt-0.5">
              {employee.first_name} {employee.last_name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">
              Certification Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={certName}
              onChange={e => setCertName(e.target.value)}
              placeholder="e.g. OSHA 10 - Construction"
              maxLength={120}
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">
              Type <span className="text-red-500">*</span>
            </label>
            <select
              value={certType}
              onChange={e => setCertType(e.target.value)}
              className={inputClass}
            >
              {CERT_TYPES.map(t => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">
              Issuing Organization
            </label>
            <input
              type="text"
              value={issuingOrg}
              onChange={e => setIssuingOrg(e.target.value)}
              placeholder="e.g. OSHA, GAF, Owens Corning"
              maxLength={120}
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">
              Certification Number
            </label>
            <input
              type="text"
              value={certNumber}
              onChange={e => setCertNumber(e.target.value)}
              placeholder="e.g. ABC-123456"
              maxLength={80}
              className={inputClass}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">
                Issued Date
              </label>
              <input
                type="date"
                value={issuedDate}
                onChange={e => setIssuedDate(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">
                Expiration Date
              </label>
              <input
                type="date"
                value={expirationDate}
                onChange={e => setExpirationDate(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Optional notes about this certification"
              className={`${inputClass} resize-none`}
            />
          </div>

          {isEdit && (
            <button
              type="button"
              onClick={handleRenew}
              className="w-full px-3 py-2 rounded-lg text-[12px] font-bold text-purple-700 bg-purple-50 hover:bg-purple-100 transition-colors"
            >
              Renew (+1 year from today)
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={saveWithRetry}
            disabled={!isValid}
            className="px-4 py-2 rounded-lg text-[13px] font-semibold text-white bg-[#1C2E5E] hover:bg-[#022b80] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Certification'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CertificationModal;
